import React, { useState } from 'react';
import parse from 'html-react-parser';
import {
    StyActionBarButton,
    StyActionBarSubmitButton,
} from '../shared/StyActionBarButton';

import ActionBar from '../shared/ActionBar';
import { AvailableTable } from 'routes/Cellar/types';
import FailedDataAlert from 'routes/ImportCsv/ImportWorkflow/components/FailedDataAlert';
import LoadedTable from './LoadedTable';
import Metadata from 'routes/ImportCsv/ImportWorkflow/components/ShowTableStep/Metadata';
import ShareForm from 'components/ShareForm/ShareForm';
import { Step } from 'routes/ImportCsv/types';
import Typography from '@mui/material/Typography';
import { UploadedFileT } from '../../hooks/uploadedFiles';
import { downloadModel } from 'routes/ImportCsv/ImportWorkflow/downloadDataHelpers';
import { getUploadedFileMetadata } from './helpers';
import { isUserTeamManty } from 'helpers/users';
import { styled } from '@mui/material/styles';
import { trackEvent } from 'helpers/trackerHelpers';
import {
    useArchiveCSVMutation,
    useDeleteTableMutation,
} from 'routes/ImportCsv/ImportWorkflow/hooks/tableMetadata';
import useGetCsvOwner from '../../hooks/getCsvOwner';
import useGetCsvSharings from '../../hooks/getCsvSharings';
import { useIsV1Table } from 'routes/ImportCsv/ImportWorkflow/hooks/helpers';
import { useNotifyCSVMessage } from 'routes/ImportCsv/ImportWorkflow/hooks/csvSnackbar';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useUpdateCsvSharings from '../../hooks/updateCsvSharings';
import { SUPPORT } from 'business/constants';
import { useUserHasRole } from 'hooks/user';
import { useNavigate } from 'react-router-dom';
import { TableT } from 'routes/Cellar/components/DomainDetails';

type Props = {
    tableData: GetDataResponse | undefined;
    latestUploadedFile: UploadedFileT | undefined;
    hasFailedDataOnLastUpload: boolean;
    selectedTable: AvailableTable;
    setStep(step: Step): void;
    canEditRights: boolean;
    dropTable: () => void;
    tableMetadata?: TableT;
};

const StyButtonGroup = styled('div', {
    name: 'StyButtonGroup',
})({
    display: 'flex',
    justifyContent: 'space-between',
    '&:not(:last-child)': {
        marginRight: '1rem',
    },
});

const ShowTableStep = ({
    tableData,
    latestUploadedFile,
    hasFailedDataOnLastUpload,
    selectedTable,
    setStep,
    canEditRights,
    dropTable,
    tableMetadata,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    const isV1Table = useIsV1Table(selectedTable);
    const userIsSupport = useUserHasRole(SUPPORT);

    const errorSnackbarNotifCallback = useNotifyCSVMessage('error');
    const successSnackbarNotifCallback = useNotifyCSVMessage('success');

    const [showAlert, setShowAlert] = useState<boolean>(true);
    const [showEditRightsModal, setShowEditRightsModal] = useState(false);

    const { data: tableSharings } = useGetCsvSharings(selectedTable);
    const { data: csvOwner } = useGetCsvOwner(selectedTable);
    const { mutate: updateCsvSharings } = useUpdateCsvSharings();
    const navigate = useNavigate();

    const emptyReactQueryTableData = (table: string) => {
        queryClient.setQueryData(
            ['getTableData', table],
            (oldData: GetDataResponse) => ({
                ...oldData,
                data: [],
            }),
        );
    };

    const useTableDelete = useDeleteTableMutation(
        queryClient,
        selectedTable,
        () => {
            successSnackbarNotifCallback({
                message: t('importCSV.success.empty', {
                    tableName: selectedTable,
                }),
            });
            emptyReactQueryTableData(selectedTable);
        },
        errorSnackbarNotifCallback,
    );
    const emptyTable = (): void => {
        useTableDelete.mutate({});
    };

    const useArchiveTable = useArchiveCSVMutation(
        queryClient,
        selectedTable,
        () => {
            successSnackbarNotifCallback({
                message: t('importCSV.success.archive', {
                    tableName: selectedTable,
                }),
            });
            emptyReactQueryTableData(selectedTable);
            navigate('/cellar/filesImport');
        },
        errorSnackbarNotifCallback,
    );
    const archiveTable = (): void => {
        useArchiveTable.mutate({});
    };

    return (
        <>
            <Typography variant="h5">{t('importCSV.dataTitle')}</Typography>
            <Metadata
                metadata={getUploadedFileMetadata(
                    latestUploadedFile,
                    tableData,
                    csvOwner,
                )}
            />
            {showAlert && selectedTable && hasFailedDataOnLastUpload && (
                <FailedDataAlert
                    setShow={setShowAlert}
                    action={() =>
                        downloadModel(
                            `${selectedTable}-import-errors`,
                            tableData ? tableData.columns : [],
                            latestUploadedFile
                                ? latestUploadedFile.failedData
                                : [],
                            hasFailedDataOnLastUpload,
                        )
                    }
                />
            )}
            {tableData && <LoadedTable backendData={tableData} />}
            <ActionBar>
                {canEditRights && (
                    <>
                        <StyButtonGroup>
                            {userIsSupport && (
                                <StyActionBarSubmitButton
                                    buttonLabel={t('importCSV.dropTable')}
                                    submit={dropTable}
                                    dialogTitle={t('common.confirm')}
                                    variant="outlined"
                                    isDestructive
                                >
                                    {t('common.deleteConfirm')}
                                </StyActionBarSubmitButton>
                            )}
                            {!tableMetadata?.isArchived && (
                                <StyActionBarSubmitButton
                                    buttonLabel={t('importCSV.archiveTable')}
                                    submit={archiveTable}
                                    dialogTitle={t('common.confirm')}
                                    variant="outlined"
                                    isDestructive
                                >
                                    {parse(t('importCSV.confirmArchiveTable'))}
                                </StyActionBarSubmitButton>
                            )}
                            {!isV1Table && (
                                <StyActionBarButton
                                    onClick={() => {
                                        trackEvent(
                                            'settings/data/csv-imports-management/existing-table/gérer-droits-accès',
                                        );
                                        setShowEditRightsModal(true);
                                    }}
                                    color="primary"
                                    variant="outlined"
                                >
                                    {t('importCSV.editTableRights')}
                                </StyActionBarButton>
                            )}
                        </StyButtonGroup>
                        <StyButtonGroup>
                            <StyActionBarSubmitButton
                                buttonLabel={t('importCSV.emptyTable')}
                                submit={emptyTable}
                                dialogTitle={t('common.confirm')}
                                variant="outlined"
                            >
                                {t('common.deleteConfirm')}
                            </StyActionBarSubmitButton>
                            <StyActionBarButton
                                variant="contained"
                                onClick={() => {
                                    trackEvent(
                                        'settings/data/csv-imports-management/existing-table/add-data-click',
                                    );
                                    setStep('selectFileStep');
                                }}
                            >
                                {t('importCSV.addCSVFile')}
                            </StyActionBarButton>
                        </StyButtonGroup>
                    </>
                )}
            </ActionBar>
            {showEditRightsModal && tableSharings && (
                <ShareForm
                    show
                    translationKey="csv"
                    owner={csvOwner}
                    shareKey={tableSharings.id}
                    orgSharing={tableSharings.orgSharing}
                    title={tableSharings.label}
                    usersSharing={tableSharings.usersSharing.filter(
                        (userSharing) =>
                            userSharing.email !== csvOwner?.email &&
                            !isUserTeamManty(userSharing.email),
                    )}
                    closeModal={() => setShowEditRightsModal(false)}
                    submit={updateCsvSharings}
                    showLeftActions={false}
                />
            )}
        </>
    );
};

export default ShowTableStep;
